import { ko } from 'date-fns/locale/ko';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
    cancelMAAAA001ScheduledPrice,
    getMAAAA001,
    getMAAAA001Detail,
    postMAAAA001ScheduledPrice,
    putMAAAA001,
    putMAAAA001ScheduledPrice,
} from '../../../services/Api';
import SideMenu from '../../../shared/components/SideMenu';
import { MENU_MANAGEMENT, MESSAGE } from '../../../shared/constants/app';
import { formatNumberWithCommas, handleFocus } from '../../../utils';

registerLocale('ko', ko);

const MAAAA001 = () => {
    const effectFromRef = useRef();

    const [data, setData] = useState([]);
    const [isPopupVisile, setIsPopupVisile] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [effectFrom, setEffectFrom] = useState(null);

    const getListData = () => {
        getMAAAA001()
            .then(({ data }) => {
                setData(data || []);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const formatPrice = (price) => (price ? formatNumberWithCommas(price) : '-');

    // Có lịch đổi giá thì in "giá hiện tại (giá dự kiến)", phần trong ngoặc in đậm.
    const renderPrice = (item, currentField, scheduledField) => (
        <>
            {formatPrice(item[currentField])}
            {item.hasScheduledChange && item[scheduledField] ? (
                <>
                    {' '}
                    <strong>({formatPrice(item[scheduledField])})</strong>
                </>
            ) : null}
        </>
    );

    const handleOpen = (goodCode) => {
        getMAAAA001Detail(goodCode)
            .then(({ data }) => {
                // Nếu đang có lịch đổi giá thì popup hiển thị giá dự kiến để sửa tiếp,
                // ngược lại hiển thị giá đang áp dụng.
                setPopupData({
                    goodCode: data.goodCode,
                    goodName: data.goodName || '',
                    originGoodName: data.goodName || '',
                    termDays: data.termDays,
                    hasScheduledChange: data.hasScheduledChange,
                    scheduledPriceSeq: data.scheduledPriceSeq,
                    scheduledEffectFrom: data.scheduledEffectFrom,
                    priceDJ: data.hasScheduledChange ? data.scheduledPriceDJ : data.currentPriceDJ,
                    priceDVMall: data.hasScheduledChange ? data.scheduledPriceDVMall : data.currentPriceDVMall,
                    priceDVMember: data.hasScheduledChange ? data.scheduledPriceDVMember : data.currentPriceDVMember,
                });
                setEffectFrom(
                    data.hasScheduledChange && data.scheduledEffectFrom
                        ? moment(data.scheduledEffectFrom, 'YYYY.MM.DD').toDate()
                        : null
                );
                setIsPopupVisile(true);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const handleClose = () => {
        setIsPopupVisile(false);
        setPopupData(null);
        setEffectFrom(null);
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        setPopupData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangePrice = (e) => {
        const { name, value } = e.target;
        const onlyDigits = value.replace(/[^\d]/g, '');
        setPopupData((prev) => ({
            ...prev,
            [name]: onlyDigits === '' ? '' : Number(onlyDigits),
        }));
    };

    const handleSave = async () => {
        if (!popupData.goodName) {
            window.alert('상품명을 입력해 주세요.');
            handleFocus('goodName');
            return;
        } else if (popupData.priceDJ === '' || popupData.priceDJ == null) {
            window.alert('덴탈잡 가격을 입력해 주세요.');
            handleFocus('priceDJ');
            return;
        } else if (!effectFrom) {
            window.alert('수정 적용일을 선택해 주세요.');
            return;
        }

        const { goodCode } = popupData;
        const pricePayload = {
            priceDJ: Number(popupData.priceDJ) || 0,
            priceDVMall: Number(popupData.priceDVMall) || 0,
            priceDVMember: Number(popupData.priceDVMember) || 0,
            effectFrom: moment(effectFrom).format('YYYYMMDD'),
        };
        const updatePayload = { ...pricePayload, goodName: popupData.goodName };

        try {
            if (moment(effectFrom).isSame(moment(), 'day')) {
                // Áp dụng từ hôm nay: sửa giá đang áp dụng, BE tự huỷ lịch đổi giá (nếu có)
                await putMAAAA001(goodCode, updatePayload);
            } else {
                // Áp dụng từ ngày tương lai: đã có lịch thì sửa lịch đó, chưa có thì tạo mới
                if (popupData.hasScheduledChange && popupData.scheduledPriceSeq) {
                    await putMAAAA001ScheduledPrice(goodCode, {
                        ...pricePayload,
                        priceSeq: popupData.scheduledPriceSeq,
                    });
                } else {
                    await postMAAAA001ScheduledPrice(goodCode, pricePayload);
                }

                // API giá dự kiến không lưu tên sản phẩm. Đổi tên thì gọi thêm API sửa sản phẩm;
                // với ngày tương lai BE chỉ cập nhật lại đúng lịch giá vừa lưu (cùng giá trị).
                if (popupData.goodName !== popupData.originGoodName) {
                    await putMAAAA001(goodCode, updatePayload);
                }
            }

            window.alert(MESSAGE.notify.updateSuccess);
            getListData();
            handleClose();
        } catch (error) {
            console.error(error);
            window.alert(MESSAGE.notify.updateError);
            // Có thể đã lưu được một phần (vd: lưu giá dự kiến xong nhưng đổi tên lỗi) nên tải lại danh sách
            getListData();
        }
    };

    const handleCancelScheduled = async () => {
        if (!popupData?.scheduledPriceSeq || !window.confirm('변경 예정 가격을 취소하시겠습니까?')) {
            return;
        }

        try {
            await cancelMAAAA001ScheduledPrice(popupData.goodCode, popupData.scheduledPriceSeq);
            window.alert('변경 예정 가격이 취소되었습니다.');
            getListData();
            handleClose();
        } catch (error) {
            console.error(error);
            window.alert(MESSAGE.notify.updateError);
        }
    };

    useEffect(() => {
        getListData();
    }, []);

    return (
        <div className="container">
            <div className="wrap">
                <SideMenu menu={MENU_MANAGEMENT} />

                <div className="content-right">
                    <div className="title title-list-notification">상품관리</div>
                    <div className="results maaaa001-list">
                        <div className="group-top-item">
                            <div className="count-item">
                                총 <span className="total">{formatNumberWithCommas(data?.length || 0)}</span>건
                            </div>
                        </div>
                        <table className="table-gray">
                            <thead>
                                <tr>
                                    <th>코드</th>
                                    <th>상품명</th>
                                    <th>덴탈잡 가격</th>
                                    <th>DVmall 가격</th>
                                    <th>DVmall 멤버십 가격</th>
                                    <th>상품 기간</th>
                                    <th>최종 수정일</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data && data.length > 0 ? (
                                    data.map((item) => (
                                        <tr key={item.goodCode}>
                                            <td>{item.goodCode}</td>
                                            <td>{item.goodName}</td>
                                            <td>{renderPrice(item, 'currentPriceDJ', 'scheduledPriceDJ')}</td>
                                            <td>{renderPrice(item, 'currentPriceDVMall', 'scheduledPriceDVMall')}</td>
                                            <td>{renderPrice(item, 'currentPriceDVMember', 'scheduledPriceDVMember')}</td>
                                            <td>{item.termDays ? `${item.termDays}일` : '-'}</td>
                                            <td>
                                                {item.hasScheduledChange ? (
                                                    <strong>{item.displayModDate}</strong>
                                                ) : (
                                                    item.currentEffectFrom
                                                )}
                                            </td>
                                            <td>
                                                <button className="edit" onClick={() => handleOpen(item.goodCode)}>
                                                    수정
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8}>데이타가 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {isPopupVisile && popupData && (
                <div id="popup" className="maaaa001">
                    <div className="overlay" onClick={handleClose} />
                    <div className="popup-content" style={{ width: '560px' }}>
                        <div className="popup-close" onClick={handleClose} />
                        <div className="popup-title">상품정보 수정</div>
                        <div className="popup-table">
                            <div className="table-row">
                                <div className="table-row-title">
                                    상품명
                                </div>
                                <div className="table-row-content">
                                    <input
                                        type="text"
                                        className="input-reason"
                                        name="goodName"
                                        value={popupData.goodName}
                                        onChange={handleChangeInput}
                                    />
                                </div>
                            </div>
                            <div className="table-row">
                                <div className="table-row-title">
                                    덴탈잡 가격
                                </div>
                                <div className="table-row-content">
                                    <input
                                        type="text"
                                        className="input-reason"
                                        name="priceDJ"
                                        value={formatNumberWithCommas(popupData.priceDJ)}
                                        onChange={handleChangePrice}
                                    />
                                </div>
                            </div>
                            <div className="table-row">
                                <div className="table-row-title">
                                    DVmall 가격
                                </div>
                                <div className="table-row-content">
                                    <input
                                        type="text"
                                        className="input-reason"
                                        name="priceDVMall"
                                        value={formatNumberWithCommas(popupData.priceDVMall)}
                                        onChange={handleChangePrice}
                                    />
                                </div>
                            </div>
                            <div className="table-row">
                                <div className="table-row-title">
                                    DVmall 멤버십 가격
                                </div>
                                <div className="table-row-content">
                                    <input
                                        type="text"
                                        className="input-reason"
                                        name="priceDVMember"
                                        value={formatNumberWithCommas(popupData.priceDVMember)}
                                        onChange={handleChangePrice}
                                    />
                                </div>
                            </div>
                            <div className="table-row">
                                <div className="table-row-title">
                                    상품 기간
                                </div>
                                <div className="table-row-content">
                                    <input
                                        type="text"
                                        className="input-reason"
                                        value={popupData.termDays ? `${popupData.termDays}일` : ''}
                                        disabled
                                    />
                                </div>
                            </div>
                            {popupData.hasScheduledChange && popupData.scheduledPriceSeq && (
                                <div className="table-row">
                                    <div className="table-row-title">
                                        변경 예정
                                    </div>
                                    <div className="table-row-content scheduled-info">
                                        <span>{popupData.scheduledEffectFrom} 적용 예정</span>
                                        <button className="cancel" onClick={handleCancelScheduled}>
                                            예정 취소
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="table-row">
                                <div className="table-row-title">
                                    수정 적용일
                                </div>
                                <div className="table-row-content">
                                    <div className="datepicker-child">
                                        <DatePicker
                                            ref={effectFromRef}
                                            onChange={(date) => setEffectFrom(date)}
                                            locale={'ko'}
                                            placeholderText={'YYYY/MM/DD'}
                                            dateFormat={'yyyy/MM/dd'}
                                            selected={effectFrom}
                                            minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                                        />
                                        <i
                                            className="icon icon-date"
                                            onClick={() => effectFromRef.current.setFocus()}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button className="popup-button" onClick={handleSave}>
                            저장
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MAAAA001;
